import * as THREE from 'three'

import { EditorUserError } from '@glass-designer/utils/editorErrors'
import BaseTool from '@glass-designer/tools/baseTool'
import MathsUtils from '@/utils/mathsUtils'

import GlassShape from '@glass-designer/model/glassShape'
import LinearRulerObject from '@glass-designer/objects/linearRulerObject'

import GlassDesignerTypes from '@glass-designer/types'


// Segment Move Tool
// Tool used when user clicked & dragged on a move button for a segment
export default class SegmentMoveTool extends BaseTool {
  constructor(editor) {
    super(editor)

    this.type = GlassDesignerTypes.ToolTypes.segmentMoveTool
    this.moveDirection = new THREE.Vector2(0, 0)
    this.moveStartPosition = new THREE.Vector2(0, 0)

    this.shapeMidpoint = new THREE.Vector2(0, 0)
    this.selectedShapeList = []
    this.selectedSegmentList = []
    this.selectedSegmentStartList = []
    this.segmentMoveType = GlassDesignerTypes.MoveSegmentTypes.move

    this.linearRulerObj = new LinearRulerObject(this.editor, {
      colour: '#FF0000',
      displayAsChange: true
    })
    this.linearRulerObj.setVisible(false)
    this.linearRulerObj.setObjectEventListener(this)
    this.addChildObject(this.linearRulerObj)
  }


  // Tool Active
  // Called when the tool is active
  onToolActive(options = {}) {
    super.onToolActive()

    this.selectedShapeList = []
    this.selectedSegmentList = []
    this.selectedSegmentStartList = []

    let selectedList = this.editor.getSelected()
    selectedList.forEach(selectedObj => {
      if (selectedObj instanceof GlassShape) {
        if (selectedObj.shapeHighlightOnly)
          return

        if(!this.selectedShapeList.find(shape => shape.id == selectedObj.id))
          this.selectedShapeList.push(selectedObj)

        selectedObj.segmentList.forEach(segment => {
          if(segment.getSegmentSelected()) {
            this.selectedSegmentList.push(segment)
          }
          if(segment.getSegmentStartSelected()) {
            this.selectedSegmentStartList.push(segment)
          }
        })
      }
    })

    if(this.selectedShapeList.length != 1)
      return

    this.shapeMidpoint.copy(this.selectedShapeList[0].calculateShapeMidpoint())

    var selectionBounds = this.editor.calculateSelectedObject2DBounds()
    var rulerMeasurePos = new THREE.Vector2(selectionBounds.min.x, selectionBounds.max.y)
    var handlePos = new THREE.Vector2(selectionBounds.min.x, selectionBounds.max.y)
    if(this.selectedSegmentList.length == 1) {
      handlePos = this.selectedSegmentList[0].getMidpoint()
      rulerMeasurePos = this.selectedSegmentList[0].startPosition
    }

    let moveHandle = this.editor.getMoveHandleObject()

    if(options.direction) {
      this.moveDirection.copy(options.direction)

      // Restrict the move handle axis to the axis we're moving
      moveHandle.setupHandles({
        horizontalEnabled: Math.abs(this.moveDirection.x) > 0.5,
        verticalEnabled: Math.abs(this.moveDirection.y) > 0.5
      })
    }
    this.moveStartPosition.copy(handlePos)

    moveHandle.setPosition(handlePos)
    moveHandle.setVisible(true)

    // Display an indicator on the start/end of the segment showing the change difference
    this.linearRulerObj.setStartPosition(rulerMeasurePos)
    this.linearRulerObj.setEndPosition(rulerMeasurePos)
    this.linearRulerObj.setOutDirection(new THREE.Vector2(1, 0))
    this.linearRulerObj.setVisible(true)
    this.linearRulerObj.updateSceneObject()

    this.updateRulerPosition(rulerMeasurePos)

    this.segmentMoveType = GlassDesignerTypes.MoveSegmentTypes.move
    var isSegmentMoveOptionsAvailable = false

    // If perpendicular movement, allow extra segment move options
    if(this.selectedSegmentList.length == 1) {
      if(options.direction) {
        if(Math.abs(options.direction.dot(this.selectedSegmentList[0].getOutDirection())) > 0.5)
          isSegmentMoveOptionsAvailable = true
      }

      // If an arc, disable the move options
      if(this.selectedSegmentList[0].type == GlassDesignerTypes.SegmentTypes.arc)
        isSegmentMoveOptionsAvailable = false
    }

    // Fill in options
    var popoverOptions = {
      value: 0,
      displayUserFixedField: false,
      displaySegmentMoveOptions: isSegmentMoveOptionsAvailable,
      segmentMoveType: this.segmentMoveType,
      isUserFixed: false,//segment.isUserFixed,
      allowPlusMinus: true,
      title: 'Move',
      addon: 'mm',
      context: this,
      updateCallback: this.updateShapeEdit,
      cancelCallback: this.cancelShapeEdit,
      applyCallback: this.applyShapeEdit
    }

    var displayOptions = {
      horizontalPosition: this._calculatePopoverHorizontalPosition()
    }

    this.editor.component.displayNumberEntryPopover(popoverOptions, options.position, displayOptions)

    this.editor.startShapeEditingMode(this.selectedShapeList[0])
  }

  // Tool Inactive
  // Called when the tool is no longer active
  onToolInactive() {
    super.onToolInactive()

    this.linearRulerObj.setVisible(false)

    this.editor.endShapeEditingMode()
    this.editor.component.hidePopover()
  }


  updateShapeEdit(context, options) {
    let maxLength = 10000   // Sanity check

    if(options.value > maxLength)
      throw new EditorUserError(`Length is too large. Must be less than ${maxLength}`)

    context.moveShapeSegments(false, options.value, options.segmentMoveType)
  }

  applyShapeEdit(context, options) {
    let maxLength = 10000   // Sanity check

    if(options.value > maxLength)
      throw new EditorUserError(`Length is too large. Must be less than ${maxLength}`)

    context.moveShapeSegments(true, options.value, options.segmentMoveType)
  }


  moveShapeSegments(applyShape, distance, moveType) {
    // NOTE: Only handles single segments / segment starts for the moment
    // In future will look at moving a group of segments around

    this.segmentMoveType = moveType

    var editingType = GlassDesignerTypes.ShapeEditingTypes.segment
    let glassShape = this.selectedShapeList[0]
    var segmentIndex = 0
    if(this.selectedSegmentList.length > 0)
      segmentIndex = glassShape.findSegmentIndexById(this.selectedSegmentList[0].id)
    else if(this.selectedSegmentStartList.length > 0) {
      segmentIndex = glassShape.findSegmentIndexById(this.selectedSegmentStartList[0].id)
      editingType = GlassDesignerTypes.ShapeEditingTypes.segmentStart
    }

    var moveDirectionType = null
    if(this.moveDirection.x < -0.5)
      moveDirectionType = GlassDesignerTypes.MoveEditDirectionTypes.left
    else if(this.moveDirection.x > 0.5)
      moveDirectionType = GlassDesignerTypes.MoveEditDirectionTypes.right
    else if(this.moveDirection.y < -0.5)
      moveDirectionType = GlassDesignerTypes.MoveEditDirectionTypes.up
    else if(this.moveDirection.y > 0.5)
      moveDirectionType = GlassDesignerTypes.MoveEditDirectionTypes.down

    // If a segment, get move type relative to the segment out direction
    if(editingType == GlassDesignerTypes.ShapeEditingTypes.segment) {
      let segOut = this.selectedSegmentList[0].getOutDirection()
      let segDir = this.selectedSegmentList[0].getDirection()

      if(segOut.dot(this.moveDirection) > 0.5)
        moveDirectionType = GlassDesignerTypes.MoveEditDirectionTypes.up
      else if(segOut.dot(this.moveDirection) < -0.5)
        moveDirectionType = GlassDesignerTypes.MoveEditDirectionTypes.down
      else if(segDir.dot(this.moveDirection) > 0.5)
        moveDirectionType = GlassDesignerTypes.MoveEditDirectionTypes.right
      else
        moveDirectionType = GlassDesignerTypes.MoveEditDirectionTypes.left
    }


    this.editor.updateShapeSegment(glassShape, segmentIndex, {
      highlightOnly: !applyShape,
      length: distance,
      isUserFixed: false,
      updateUserFixed: false,
      editingType: editingType,
      segmentMoveType: moveType,
      moveOffset: distance,
      moveDirection: moveDirectionType,
    })

    // Update move handle + ruler
    if(!applyShape) {
      let moveHandle = this.editor.getMoveHandleObject()
      let segment = this.editor.highlightShape.getSegmentForIndex(segmentIndex)

      var handlePosition = segment.startPosition.clone()
      var rulerPosition = segment.startPosition.clone()
      if(editingType == GlassDesignerTypes.ShapeEditingTypes.segment) {
        rulerPosition = segment.startPosition.clone()

        // Hack: If extending, there is a chance a new segment was created & this is the fixed segment
        // If so, use the next segment
        if(moveType == GlassDesignerTypes.MoveSegmentTypes.extend) {
          if(rulerPosition.distanceTo(this.linearRulerObj.startPosition) < Math.abs(distance / 2))
            rulerPosition = segment.endPosition.clone()
        }

        handlePosition = this.moveStartPosition.clone().addScaledVector(this.moveDirection, distance)
      }

      moveHandle.setPosition(handlePosition)
      this.updateRulerPosition(rulerPosition)

      this.editor.flagRenderRequired()
    }
  }

  cancelShapeEdit(context) {
    context.editor.cancelActiveTool()
  }


  onReceiveObjectEvent(object, eventType, data) {
    let moveHandle = this.editor.getMoveHandleObject()
    
    if(eventType == GlassDesignerTypes.ObjectEventTypes.objectMoved) {
      if(object == moveHandle) {
        this.updateRulerPosition(moveHandle.position)

        // Calculate distance
        let distance = Math.round(MathsUtils.calculateDistanceToPlane2D(this.moveDirection, this.moveStartPosition, moveHandle.position))

        // Update popover
        this.editor.component.updateNumberEntryPopover(distance)

        // Move shape segments
        this.moveShapeSegments(false, distance, this.segmentMoveType)
      }
    }
  }


  updateRulerPosition(position) {
    var outDir = position.clone().sub(this.linearRulerObj.startPosition)
    outDir.normalize()
    outDir.set(outDir.y, -outDir.x)

    // Set to always face away from the shape
    if(MathsUtils.calculateDistanceToPlane2D(outDir, position, this.shapeMidpoint) > 0)
      outDir.negate()

    this.linearRulerObj.setEndPosition(position)
    this.linearRulerObj.setOutDirection(outDir)
    this.linearRulerObj.updateSceneObject()

  }
}
